// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from "react";
import { Stack, useMediaQuery, useTheme } from "@mui/material";
import {
  GridColDef,
  GridPaginationModel,
  GridActionsCellItem,
  GridRowId,
} from "@mui/x-data-grid";
import { DeleteConfirmModal, Table } from "../../molecules";
import {
  IResource,
  useGetResourceList,
  DeleteResourceRequest,
  useDeleteResource,
  useAuthorization,
} from "../../../hooks";
import { Loading } from "../../atoms";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { downloadExcel } from "../../../util/downloadExcel";

export type MessageListingTableProps = {
  onLanguageClick?: (row: IResource, lang: "tr" | "en") => void;
  isModalOpen?: boolean;
};
export const MessageListingTable = ({
  onLanguageClick,
  isModalOpen,
}: MessageListingTableProps) => {
  const theme = useTheme();
  const { showDelete, showUpdate } = useAuthorization();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [queryOptions, setQueryOptions] = React.useState({});
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  });
  const { data, mutate: getResourceList, isLoading } = useGetResourceList();
  // const tableData = data?.data;
  const [tableData, setTableData] = useState<PagingResponse<Array<any>>>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(0);
  const setSnackbar = useSetSnackBar();
  const { mutate: resourceDelete, isLoading: isDeleteLoading } =
    useDeleteResource();
  const [shouldRefresh, setShouldRefresh] = React.useState(false);


  useEffect(() => {
    if (!isModalOpen) {
      const requestPayload = {
        size: paginationModel.pageSize,
        page: paginationModel.page,
        orderBy: "CreateDate",
        orderByDesc: true,
        ...getQueryParams(queryOptions),
      };
      if (queryOptions?.field && queryOptions?.value !== undefined) {
        requestPayload[queryOptions.field] = queryOptions.value;
      }
      getResourceList(
        requestPayload,
        {
          onSuccess: (data) => {
            if (data.isSuccess) {
              setTableData(data?.data);
            } else {
              setTableData(data?.data);
              setSnackbar({
                severity: "error",
                description: data.message,
                isOpen: true,
              });
            }
          },
          onError: () => {
            setSnackbar({
              severity: "error",
              description: "İşlem sırasında bir hata oluştu",
              isOpen: true,
            });
          },
        });
    }
  }, [
    getResourceList,
    paginationModel.page,
    paginationModel.pageSize,
    setSnackbar,
    queryOptions,
    shouldRefresh,
    isModalOpen
  ]);

  const handleChangePagination = (model: GridPaginationModel) => {
    setPaginationModel(model);
    setTableData(undefined);
    getResourceList(
      {
        size: paginationModel.pageSize,
        page: paginationModel.page,
        orderBy: "CreateDate",
        orderByDesc: true,
      },
      {
        onSuccess: (data) => {
          if (data.isSuccess) {
            setTableData(data?.data);
          } else {
            setSnackbar({
              severity: "error",
              description: data.message,
              isOpen: true,
            });
          }
        },
        onError: () => {
          setSnackbar({
            severity: "error",
            description: "İşlem sırasında bir hata oluştu",
            isOpen: true,
          });
        },
      }
    );
  };

  const deleteRow = React.useCallback(
    (id: GridRowId) => () => {
      setIsDeleteModalOpen(true);
      setSelectedRowId(Number(id));
    },
    []
  );

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    const deleteRequest: DeleteResourceRequest = { id: selectedRowId };

    resourceDelete(deleteRequest, {
      onSuccess: (data: any) => {
        if (data.isSuccess) {
          setShouldRefresh(prev => !prev);
          setSnackbar({
            severity: "success",
            isOpen: true,
            description: data.message,
          });
        } else {
          setSnackbar({
            severity: "error",
            description: data.message,
            isOpen: true,
          });
        }
      },
      onError: () => {
        setSnackbar({
          severity: "error",
          description: "İşlem sırasında bir hata oluştu",
          isOpen: true,
        });
      },
    });

    handleCloseDeleteModal();
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "actions",
        type: "actions",
        width: 80,
        getActions: (params) => [
          !!showDelete ? (
            <GridActionsCellItem
              label="Sil"
              onClick={deleteRow(params?.id)}
              showInMenu
            />
          ) : (<></>)
        ]
      },
      { field: "resourceTypeName", headerName: "Kaynak Tipi Adı", width: 180, },
      { field: "key", headerName: "Mesaj Kodu", width: 150 },
      {
        field: "value",
        headerName: "Mesaj Açıklaması",
        width: 200,
        renderCell: (params) => (
          <div
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => showUpdate && onLanguageClick?.(params.row, "tr")}
          >
            {params.value}
          </div>
        ),
      },
      { field: "language", headerName: "Dil", width: 100, },
      {
        field: "value2",
        headerName: "Mesaj Açıklaması (EN)",
        width: 200,
        renderCell: (params) => (
          <div
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => showUpdate && onLanguageClick?.(params.row, "en")}
          >
            {params.value}
          </div>
        ),
      },
      { field: "language2", headerName: "Dil (EN)", width: 100, },
      { field: "status", headerName: "Durum", width: 100, valueGetter: (params) => (params.row?.status === "ACTIVE" ? "Aktif" : "Pasif") },
    ];
  }, [deleteRow, showDelete, onLanguageClick, showUpdate]);

  const onSave = () => {
    const requestPayload = {
      size: -1,
      page: 0,
      orderBy: "CreateDate",
      orderByDesc: true,
      ...getQueryParams(queryOptions),
    };

    if (queryOptions?.field && queryOptions?.value !== undefined) {
      requestPayload[queryOptions.field] = queryOptions.value;
    }

    getResourceList(
      requestPayload,
      {
        onSuccess: (data) => {
          if (data.isSuccess) {
            function fieldValue(data) {
              return data.map((item) => {
                return {
                  "Mesaj Kodu": item?.key,
                  "Mesaj Açıklaması": item?.value,
                  "Mesaj Türü": item?.resourceTypeName,
                  "Entegrasyon Mesaj Kodu": item?.order,
                  "Dil": item?.language

                };
              });
            }
            const refactoredData = fieldValue(data?.data?.result);
            downloadExcel(refactoredData || [], "Mesaj Listesi");
          } else {
            setSnackbar({
              severity: "error",
              description: data.message,
              isOpen: true,
            });
          }
        },
        onError: () => {
          setSnackbar({
            severity: "error",
            description: "İşlem sırasında bir hata oluştu",
            isOpen: true,
          });
        },
      }
    );
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const getQueryParams = (filters) => {
    const queryParams = {};

    for (let key in filters) {
      if (filters[key]?.toString()?.length >= 1) {
        queryParams[key] = filters[key];
      }
    }

    return queryParams;
  };

  const handleFilterChange = debounce((filters) => {
    if (filters.clearFilter) {
      return setQueryOptions({});
    }

    const queryParams = getQueryParams(filters);

    const allValuesAtLeastThreeChars = Object.values(queryParams).every((value) => value?.toString().length >= 3);
    const atLeastOneValueThreeChars = Object.values(queryParams).some((value) => value?.toString().length >= 3);

    const previousKeysLength = Object.keys(previousQueryParams.current).length;
    const currentKeysLength = Object.keys(queryParams).length;

    // Eğer queryParams bir önceki ile aynıysa işlem yapma
    if (JSON.stringify(previousQueryParams.current) === JSON.stringify(queryParams)) {
      return;
    }

    if ((allValuesAtLeastThreeChars || (atLeastOneValueThreeChars && previousKeysLength > currentKeysLength))) {
      setQueryOptions(queryParams);
    }

    // Son olarak, mevcut queryParams değerini bir önceki değer olarak set edin
    previousQueryParams.current = queryParams;

  }, 500);
  const hasActiveFilters = Object?.keys(queryOptions)?.length > 0;
  // Komponent içerisinde
  const previousQueryParams = React.useRef({});

  const hasLoading = isLoading || isDeleteLoading;
  return (
    <>
      {hasLoading && <Loading />}
      <Stack flex={1} p={2}>

        <>
          <Table
            columnVisibilityConfigKey="messageListingColumnVisibilityKey"
            handleFilterChange={handleFilterChange}
            hasActiveFilters={hasActiveFilters}
            paginationModel={paginationModel}
            onPaginationModelChange={handleChangePagination}
            paginationMode="server"
            rowCount={tableData?.totalItems || 0}
            sx={{ width: isDesktop ? 1308 : window.innerWidth - 50 }}
            isRowSelectable={() => false}
            // disableColumnMenu
            rows={tableData?.result || []}
            columns={columns}
            exportFileName="Mesaj Listesi"
            onSave={() => onSave()}
          />
          <DeleteConfirmModal
            isOpen={isDeleteModalOpen}
            onClose={handleCloseDeleteModal}
            onConfirm={() => handleDeleteConfirm()}
          ></DeleteConfirmModal>
        </>

      </Stack>
    </>
  );
};
